import type { ReactNode } from 'react';

import { MapViewerControlCluster, type MapViewerControlItem } from '@workspace/silk-ui';

import { cn } from '../utils/cn';
import {
  MapWorkbench,
  MapWorkbenchRuntimePreview,
  type MapWorkbenchMode,
  type MapWorkbenchProps,
  type MapWorkbenchRuntimePreviewProps,
} from './map-workbench';

export interface MapViewProps extends MapWorkbenchRuntimePreviewProps {
  className?: string;
  mapClassName?: string;
  controlsSlot?: ReactNode;
  footerSlot?: ReactNode;
}

export function MapView({
  className,
  mapClassName,
  controlsSlot,
  footerSlot,
  ...previewProps
}: MapViewProps) {
  return (
    <div className={cn('relative h-full min-h-0 w-full overflow-hidden rounded-xl border border-border/60 bg-muted/5', className)}>
      <div className={cn('absolute inset-0', mapClassName)}>
        <MapWorkbenchRuntimePreview {...previewProps} />
      </div>
      {controlsSlot ? (
        <div
          className="absolute left-3 top-3 z-20"
          style={{ left: 'calc(env(safe-area-inset-left, 0px) + 0.75rem)' }}
        >
          {controlsSlot}
        </div>
      ) : null}
      {footerSlot ? (
        <div className="pointer-events-none absolute bottom-3 left-3 z-20 max-w-[min(36rem,calc(100%-1.5rem))]">
          <div className="pointer-events-auto rounded-md border border-border bg-card/92 px-3 py-2 shadow-lg backdrop-blur-sm">
            {footerSlot}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export interface MapWidgetViewProps extends Omit<MapViewProps, 'scrollZoomEnabled'> {
  scrollZoomEnabled?: boolean;
}

export function MapWidgetView({ scrollZoomEnabled = false, ...props }: MapWidgetViewProps) {
  return <MapView {...props} scrollZoomEnabled={scrollZoomEnabled} />;
}

export interface MapAuthoringViewProps extends MapViewProps {
  toolItems: MapViewerControlItem[];
  onToolAction: (actionId: string) => void;
  toolClusterClassName?: string;
}

export function MapAuthoringView({
  toolItems,
  onToolAction,
  toolClusterClassName,
  controlsSlot,
  ...props
}: MapAuthoringViewProps) {
  const toolbar = (
    <div className="flex flex-col gap-2">
      <MapViewerControlCluster
        mode="view"
        items={toolItems}
        onAction={onToolAction}
        className={toolClusterClassName}
      />
      {controlsSlot}
    </div>
  );
  return <MapView {...props} controlsSlot={toolbar} />;
}

export interface MapWorkspaceViewProps extends Omit<MapWorkbenchProps, 'defaultMode'> {
  mode?: MapWorkbenchMode;
}

export function MapWorkspaceView({ mode = 'view', ...props }: MapWorkspaceViewProps) {
  return <MapWorkbench {...props} defaultMode={mode} />;
}

export type { MapViewerControlItem };
